package com.exura.features.firebase.domain.repository.versioning

import com.exura.features.firebase.domain.model.SerializableDomainModel
import com.exura.features.firebase.domain.model.versioning.Version
import com.exura.features.firebase.domain.model.versioning.VersionMetadata
import com.exura.features.firebase.domain.repository.SerializableDomainModelRepository
import dev.gitlive.firebase.firestore.CollectionReference
import dev.gitlive.firebase.firestore.DocumentReference
import dev.gitlive.firebase.firestore.FieldPath
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.map
import kotlinx.datetime.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime

abstract class Versioned<T : SerializableDomainModel, M : VersionMetadata>(
    private val itemRepository: SerializableDomainModelRepository<T>,
    private val versionBaseRepository: SerializableDomainModelRepository<Version<T, M>>,
    private val getDataPrefix: suspend () -> Result<DocumentReference>,
    private val versionCollectionName: String,
) : VersionedRepository<T, M> {

    abstract suspend fun createItemMetadata(item: T) : M

    private suspend fun createRootVersion(
        item: T,
    ): Version<T, M> {
        val rootId = item.firebaseDocId ?: throw IllegalStateException("Can't create version for non-id item.")
        return Version(
            firebaseDocPrefix = getVersionsCollectionReference(),
            firebaseDocId = rootId,
            rootVersionId = rootId,
            parentVersionId =null,
            isActive = true,
            metadata = createItemMetadata(item),
            contentDocPrefix = item.firebaseDocPrefix ?: throw IllegalStateException("Can't create version for non-id item."),
            createdAt = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()),
        )
    }

    private suspend fun createChildVersion(
        item: T,
        previousVersion: Version<T, M>,
    ) : Version<T, M> = Version(
        firebaseDocPrefix = getVersionsCollectionReference(),
        firebaseDocId = item.firebaseDocId ?: throw IllegalStateException("Can't create version for non-id item."),
        rootVersionId = previousVersion.rootVersionId,
        parentVersionId = previousVersion.firebaseDocId ?: throw IllegalStateException("Can't create child version for non-id version."),
        isActive = true,
        metadata = createItemMetadata(item),
        contentDocPrefix = previousVersion.contentDocPrefix,
        createdAt = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()),
    )

    override suspend fun getVersionsCollectionReference(): CollectionReference =
        getDataPrefix().getOrThrow().collection(versionCollectionName)

    override suspend fun create(item: T, dest: CollectionReference): Result<Version<T, M>> = runCatching {
        val newItem = itemRepository.create(item, dest = dest).getOrThrow()
        val newItemId = newItem.firebaseDocId ?: throw IllegalStateException("Can't have null ID after item creation")
        val rootVersion = createRootVersion(newItem)
        versionBaseRepository.set(rootVersion, dest = getVersionsCollectionReference().document(newItemId))
        rootVersion.setLocalContent(newItem)

        rootVersion
    }

    suspend fun read(source: DocumentReference, withContent: Boolean): Result<Version<T, M>> = runCatching {
        val version = versionBaseRepository.read(source = getVersionsCollectionReference().document(source.id)).getOrThrow()
        if (withContent) {
            version.getContent(repository = itemRepository)
        }
        version
    }

    override suspend fun read(source: DocumentReference): Result<Version<T, M>> = read(source, withContent = false)

    override suspend fun delete(dest: DocumentReference): Result<Boolean> = runCatching {
        val versionRef = getVersionsCollectionReference().document(dest.id)
        versionRef.update(Pair("isActive", false))
        true
    }

    override suspend fun update(item: T, dest: DocumentReference): Result<Version<T, M>> = runCatching {
        val previousVersion = read(dest).getOrThrow()
        val newItem = itemRepository.create(item, dest = dest.parent).getOrThrow()
        val newItemId = newItem.firebaseDocId ?: throw IllegalStateException("Can't have null ID after item creation")
        val childVersion = createChildVersion(newItem, previousVersion = previousVersion)
        versionBaseRepository.set(childVersion, dest = getVersionsCollectionReference().document(newItemId))
        previousVersion.getFirebaseDocRef().update(Pair("isActive", false))
        childVersion.setLocalContent(newItem)

        childVersion
    }

    override suspend fun set(item: T, dest: DocumentReference): Result<Version<T, M>> = update(item, dest)


    override suspend fun list(dest: CollectionReference): Flow<List<Version<T, M>>> {
        val versionsCollectionRef = getVersionsCollectionReference()

        val activeVersionsQuery = versionsCollectionRef.where { "isActive" equalTo true }

        return activeVersionsQuery.snapshots.map { snapshot ->
            snapshot.documents.mapNotNull { doc ->
                try {
                    versionBaseRepository.read(doc.reference).getOrThrow()
                } catch (e: Exception) {
                    null
                }
            }
        }
    }
}
